<script lang="ts" setup>
import { inject, computed, watch, ref, onUnmounted } from "vue"
import { v4 as uuidv4 } from "uuid"
import { createPoint, createLabel, createSize } from "@p-v/utils/factory"
import type { Point, Size, Icon, Label } from "@p-v/packages/Map/map"

const props = withDefaults(
  defineProps<{
    color: string
    width: number
  }>(),
  {
    color: () => "#409EFF",
    width: () => 2,
  },
)

const bmap = inject("bmap") as any
const trackPoints = ref<{ lat: number; lng: number }[]>([])

const canvasOverlay = ref<any>()

const gps2xy = (map: any, pos: { lat: number; lng: number }) => map.pointToOverlayPixel(createPoint(window.BMapGL, pos))

const baseZoom = 12
let scaleFactor = 1
function updateScaleFactor(map) {
  const currentZoom = map.getZoom()
  scaleFactor = Math.pow(2, currentZoom - baseZoom)
  console.log("scaleFactor", scaleFactor)
}

const render = () => {
  class PathOverlay extends window.BMapGL.Overlay {
    constructor(coords) {
      super()
      this._points = coords
    }

    updateCoords(coords) {
      this._points = coords
      this.draw()
    }

    initialize(map) {
      this._map = map
      this._canvas = document.createElement("canvas")
      this._canvas.style.position = "absolute"
      const size = map.getSize()
      this._canvas.width = size.width
      this._canvas.height = size.height
      // 将 canvas 添加到地图的 labelPane（或其它适合的 pane）中
      map.getPanes().markerPane.appendChild(this._canvas)
      this._ctx = this._canvas.getContext("2d")
      return this._canvas
    }

    draw() {
      // 当地图尺寸变化时更新 canvas 尺寸
      const size = this._map.getSize()
      this._canvas.width = size.width
      this._canvas.height = size.height

      const points = this._points.map(point => gps2xy(bmap.value, point))

      // 清除画布
      this._ctx.clearRect(0, 0, this._canvas.width, this._canvas.height)

      // 绘制路径
      this._ctx.strokeStyle = props.color
      this._ctx.lineWidth = props.width
      this._ctx.beginPath()
      points.forEach((point, index) => {
        const { x, y } = point
        if (index === 0) {
          this._ctx.moveTo(x, y)
        } else {
          this._ctx.lineTo(x, y)
        }
      })
      this._ctx.stroke()
    }
  }

  if (!canvasOverlay.value) {
    updateScaleFactor(bmap.value)
    bmap.value.addEventListener("zoomend", () => {
      updateScaleFactor(bmap.value)
    })
    canvasOverlay.value = new PathOverlay(trackPoints.value)
    bmap.value.addOverlay(canvasOverlay.value)
  } else {
    canvasOverlay.value.updateCoords(trackPoints.value)
  }
}

watch(
  () => bmap.value,
  () => {
    if (!bmap.value) return
    // 动态导入updateScaleFactor, PathOverlay
    render()
  },
)

watch(
  () => trackPoints.value,
  () => {
    if (!bmap.value) return
    // 动态导入updateScaleFactor, PathOverlay
    render()
  },
)

onUnmounted(() => {
  if (canvasOverlay.value) {
    bmap.value.removeOverlay(canvasOverlay.value)
  }
})

defineExpose({
  setData: (opt: { data: { lat: number; lng: number }[] }) => {
    trackPoints.value = opt.data
    if (canvasOverlay.value) {
      canvasOverlay.value.updateCoords(opt.data)
    }
  },
})
</script>
